<template>
  <div
    :class="[
      'transition-input text-input',
      hasFocus && !readonly && 'active',
      value !== null && value !== void 0 && value && 'filled',
      error && 'error',
      disabled && 'disabled',
      `type-${type}`,
    ]"
  >
    <b-form-group
      class="mb-0 text8"
      :label="label"
      :label-for="id"
      :description="description"
    >
      <div class="position-relative">
        <slot name="pre" />

        <input
          :class="[type === 'password' && !showPassword && 'password']"
          :id="id"
          :value="value"
          :type="nativeType"
          :disabled="disabled"
          :readonly="readonly"
          :placeholder="placeholder"
          :min="min"
          :max="max"
          :autofocus="autofocus"
          :inputmode="inputmode"
          @input="onInput"
          @keydown="$emit('keydown', $event)"
          ref="$input"
          autocomplete="off"
          @focus="onFocus"
          @blur="onBlur"
        />

        <div class="slot-right">
          <FontIcon
            v-if="type === 'password'"
            :name="`ui_eye_${showPassword ? 'on' : 'off'}`"
            class="show-password"
            @click.native="showPassword = !showPassword"
            ref="showPassword"
          />
          <div class="help" v-else-if="textTooltip">
            <FontIcon name="ui_info" class="icon" :id="`tooltip-${id}`" />
            <b-tooltip
              class="text4"
              :show.sync="showTooltip"
              :target="`tooltip-${id}`"
              placement="top"
              triggers="click"
            >
              {{ textTooltip }}
            </b-tooltip>
          </div>
          <FontIcon v-else name="ui_status_error" class="icon icon-error" />

          <div v-if="$slots.right" class="slot-right-content">
            <slot name="right" />
          </div>
        </div>
      </div>
    </b-form-group>
    <InputError v-if="!hideError" :error="error" />
    <TextValidator
      v-if="policy"
      :title="policy.text"
      :value="value"
      :rules="policies"
    />
  </div>
</template>

<style lang="scss">
.text-input {
  position: relative;

  .form-group {
    position: relative;
  }

  .help {
    position: absolute;
    right: 0;
    transform: translateY(-50%);
    z-index: 1;
    .icon {
      cursor: pointer;
      font-size: rem(24px);
      @include setColor(--color-icon1);
    }
  }

  .slot-right {
    position: absolute;
    right: 0;
    top: rem(27px);
    transform: translateY(-50%);
    z-index: 1;
    margin-right: rem(15px);
  }

  .slot-right-content {
    padding-left: rem(5px);
    font-size: rem(12px);
    @include setColor(--color-text);
  }

  .show-password {
    cursor: pointer;
    position: absolute;
    right: 100%;
    top: 50%;
    transform: translateY(-25%);
    font-size: rem(20px);

    &:hover {
      opacity: 0.5;
    }
  }

  // ie11 fix
  &[type="password"]::-ms-clear,
  &[type="password"]::-ms-reveal {
    display: none;
  }
  &::-ms-clear,
  &::-ms-reveal {
    display: none;
  }
}
</style>

<script>
import FontIcon from "../../FontIcon/FontIcon";
import InputError from "../InputError/InputError";

import TextValidator from "../../validators/Text";

export default {
  components: {
    FontIcon,
    InputError,
    TextValidator,
  },

  props: {
    id: {
      type: String,
      required: true,
    },
    value: {
      type: String,
      default: "",
    },
    type: {
      type: String,
      default: "text",
      validator(type) {
        return ["date", "number", "text", "email", "password"].includes(type);
      },
    },
    label: {
      type: String,
      required: false,
    },
    description: {
      type: String,
      required: false,
    },
    placeholder: {
      type: String,
      required: false,
    },
    autofocus: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      required: false,
    },
    maxLength: {
      type: Number,
      default: null,
    },
    mandatory: {
      type: Boolean,
      default: false,
    },
    hideError: {
      type: Boolean,
      default: false,
    },
    policy: {
      validator: prop =>
        (typeof prop === "object" && prop.type && prop.text) || prop === null,
      required: false,
      default: null,
    },
    policies: {
      type: Array,
      default: () => [],
    },
    textTooltip: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      showPassword: false,
      hasFocus: false,
      showTooltip: false,
    };
  },

  computed: {
    nativeType() {
      switch (this.type) {
        case "date":
        case "email":
        case "number":
          return this.type;

        case "password":
          return this.showPassword ? "text" : "password";

        default:
          return "text";
      }
    },
    inputmode() {
      switch (this.type) {
        case "email":
        case "date":
        case "number":
          return this.type;

        default:
          return "text";
      }
    },
    min() {
      return this.type === "number" ? 0 : null;
    },
    max() {
      // max = 9 for length 1, 99 for length 2, 999 for length 3, ...
      return this.type === "number" && this.maxLength
        ? Math.pow(10, this.maxLength) - 1
        : null;
    },
  },

  methods: {
    onInput(value) {
      const strippedValue = this.maxLength
        ? value.target.value.slice(0, this.maxLength)
        : value.target.value;

      this.$emit("update:value", strippedValue);
      this.$forceUpdate();
    },

    onFocus() {
      this.hasFocus = true;
      this.$emit("focus");
    },

    onBlur() {
      this.hasFocus = false;
      this.$emit("blur");
    },
  },

  mounted() {
    if (this.value) {
      this.hasFocus = true;
    }
  },
};
</script>
